import React, { useState, useEffect, useCallback, useRef } from "react";
import { getAuthHeaders } from "../utils/auth";
import AiPanel from "./AiPanel";
import { useLanguage } from "../utils/i18n";

const API = "/api";

export default function BookEditorPage({
  bookId, onBack, showToast, isMobile,
  notes, onAddNote, onDeleteNote, onRenameNote, onUpdateNote,
  subfolders, onAddSubfolder, onDeleteSubfolder, onMoveNote,
  onMoveFolderToParent, folderOrder, onReorderFolderOrder,
}) {
  const { t } = useLanguage();
  const [savedHtml, setSavedHtml] = useState("");
  const [title, setTitle]         = useState("");
  const [mode, setMode]           = useState("preview"); // "preview" | "edit"
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [panelWidth, setPanelWidth] = useState(420);
  const iframeRef   = useRef(null);
  const writtenRef  = useRef(false); // track if we've written initial html to iframe

  useEffect(() => {
    const load = async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const res  = await fetch(`${API}/html-books/${bookId}`, { headers: authHeaders });
        const data = await res.json();
        if (!res.ok) { setError(data.error || "Failed to load book."); return; }
        setTitle(data.title || "");
        setSavedHtml(data.html || "");
      } catch {
        setError("Network error.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [bookId]);

  // Write HTML into iframe imperatively once (and only once) after content loads,
  // so React never re-renders / reloads the iframe while the user edits.
  const writeToIframe = useCallback((html) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    writtenRef.current = true;
  }, []);

  useEffect(() => {
    if (!savedHtml || writtenRef.current) return;
    // iframe may not be mounted yet — wait one tick
    const t = setTimeout(() => writeToIframe(savedHtml), 0);
    return () => clearTimeout(t);
  }, [savedHtml, writeToIframe]);

  // Toggle designMode when switching tabs
  useEffect(() => {
    if (!writtenRef.current) return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.designMode = mode === "edit" ? "on" : "off";
  }, [mode]);

  const save = useCallback(async () => {
    const doc = iframeRef.current?.contentDocument;
    const currentHtml = doc ? doc.documentElement.outerHTML : savedHtml;
    setSaving(true);
    try {
      const authHeaders = await getAuthHeaders();
      await fetch(`${API}/html-books/${bookId}`, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ html: currentHtml, title }),
      });
      setSavedHtml(currentHtml);
      showToast("Book saved! 📖");
    } catch {
      showToast("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [bookId, savedHtml, title, showToast]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    const onMouseMove = (e) => setPanelWidth(Math.max(300, Math.min(700, startWidth + (startX - e.clientX))));
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const pseudoBook = { id: bookId, filename: title, total_pages: 1 };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f5f7fb" }}>
        <span style={{ fontSize: 18, color: "#64748b" }}>{t("loading")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 16 }}>
        <span style={{ color: "#ef4444", fontSize: 16 }}>{error}</span>
        <button onClick={onBack} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #e2e8f0", cursor: "pointer" }}>{t("back")}</button>
      </div>
    );
  }

  return (
    <div className="book-editor-page">
      {/* ── Top bar ─────────────────────────────── */}
      <div className="bep-header">
        <button className="bep-back" onClick={onBack}>{t("backHome")}</button>

        <input
          type="text"
          className="bep-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("bookEditorTitlePlaceholder")}
        />

        <div className="bep-tabs">
          <button className={`bep-tab ${mode === "preview" ? "active" : ""}`} onClick={() => setMode("preview")}>
            {t("bookPreviewBtn")}
          </button>
          <button className={`bep-tab ${mode === "edit" ? "active" : ""}`} onClick={() => setMode("edit")}>
            {t("bookEditModeBtn")}
          </button>
        </div>

        {mode === "edit" && (
          <span className="bep-edit-hint">{t("bookEditHint")}</span>
        )}

        <button className="bep-save" onClick={save} disabled={saving}>
          {saving ? t("savingLabel") : t("bookSaveBtn")}
        </button>
      </div>

      {/* ── Content area ────────────────────────── */}
      <div className="bep-body">
        {/* Single iframe — always mounted, designMode toggled imperatively */}
        <iframe
          ref={iframeRef}
          className={`bep-iframe${mode === "edit" ? " bep-iframe--edit" : ""}`}
          title="book-content"
          sandbox="allow-same-origin"
        />

        {!isMobile && (
          <>
            <div className="resize-handle" onMouseDown={handleMouseDown} />
            <AiPanel
              book={pseudoBook}
              api={API}
              width={panelWidth}
              showToast={showToast}
              notes={notes}
              onAddNote={onAddNote}
              onDeleteNote={onDeleteNote}
              onRenameNote={onRenameNote}
              onUpdateNote={onUpdateNote}
              subfolders={subfolders || []}
              onAddSubfolder={onAddSubfolder}
              onDeleteSubfolder={onDeleteSubfolder}
              onMoveNote={onMoveNote}
              onMoveFolderToParent={onMoveFolderToParent}
              folderOrder={folderOrder}
              onReorderFolderOrder={onReorderFolderOrder}
            />
          </>
        )}
      </div>
    </div>
  );
}
